import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { notification, Tooltip } from 'antd';
import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useAppDispatch } from '../../../store/hooks';
import {
  useGetVariantAssessmentArgumentsQuery,
  useGetVariantInterpretationZoneQuery,
  useRecalculateAutoAssessmentArgumentsMutation,
} from '../../api/variantApi';
import { NOTIFICATION_MESSAGES, VARIANT_STAGE_TYPES } from '../constants';
import { useVariantAccess } from '../hooks/useVariantAccess';
import {
  clearFrequencyDataNeedsRecalculation,
  selectFrequencyDataNeedsRecalculation,
  selectIsRecalculatingArguments,
} from '../redux';

// The backend recalculation endpoint only supports 'frequency_data' for now.
// Extend this set when other stages get their own assessment condition tree root node.
const SUPPORTED_AUTO_RECALCULATE_STAGES = new Set<string>([
  VARIANT_STAGE_TYPES.FREQUENCY_DATA,
  VARIANT_STAGE_TYPES.MOLECULAR_IMPACT,
]);

interface RecalculateArgumentsButtonProps {
  stageType: string;
}

const RecalculateArgumentsButton: React.FC<RecalculateArgumentsButtonProps> = ({
  stageType,
}) => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const variantId = params.vid as string;
  const { canEditWorkflow } = useVariantAccess();

  const frequencyDataNeedsRecalculation = useSelector(
    selectFrequencyDataNeedsRecalculation,
  );
  const isRecalculating = useSelector(selectIsRecalculatingArguments);

  // Frequency data: only show the button when the flag says data changed.
  // Molecular impact (and any other supported stage): always show it so
  // users can recalculate at will.
  const isFrequencyDataStage = stageType === VARIANT_STAGE_TYPES.FREQUENCY_DATA;
  const shouldShowButton =
    SUPPORTED_AUTO_RECALCULATE_STAGES.has(stageType) &&
    (!isFrequencyDataStage || frequencyDataNeedsRecalculation);

  const [recalculateAutoAssessmentArgumentsMutation] =
    useRecalculateAutoAssessmentArgumentsMutation();

  // RTK Query hooks for refetching
  const { refetch: refetchArguments } = useGetVariantAssessmentArgumentsQuery(
    { variant_id: variantId },
    { skip: !variantId },
  );
  const { refetch: refetchInterpretationZone } =
    useGetVariantInterpretationZoneQuery(
      { variant_id: variantId },
      { skip: !variantId },
    );

  if (!shouldShowButton) {
    return null;
  }

  const handleRecalculate = async () => {
    if (isRecalculating) {
      return;
    }

    try {
      await recalculateAutoAssessmentArgumentsMutation({
        variant_id: variantId,
        stage_type: stageType,
      }).unwrap();

      // After successful recalculation: reload all relevant data
      await Promise.all([refetchArguments(), refetchInterpretationZone()]);

      // Only NOW clear the flag – after all data is refreshed in Redux.
      // This ensures the Interpretation component has the latest arguments
      // before the button disappears. Molecular impact has no such flag:
      // its button is always shown.
      if (isFrequencyDataStage) {
        dispatch(clearFrequencyDataNeedsRecalculation());
      }

      notification.success({
        message: NOTIFICATION_MESSAGES.SUCCESS,
        description: NOTIFICATION_MESSAGES.ARGUMENTS_RECALCULATED,
      });
    } catch (error) {
      console.error('Error recalculating arguments:', error);
      notification.error({
        message: NOTIFICATION_MESSAGES.ERROR,
        description: NOTIFICATION_MESSAGES.RECALCULATE_ARGUMENTS_FAILED,
      });
    }
  };

  return (
    <Tooltip title="Update autocalculated arguments. Manually selected arguments will not be overwritten.">
      <button
        type="button"
        className="recalculate-arguments-btn"
        onClick={handleRecalculate}
        disabled={isRecalculating || !canEditWorkflow}
        aria-label="Recalculate auto-calculated arguments"
      >
        <FontAwesomeIcon icon={faArrowsRotate} spin={isRecalculating} />
      </button>
    </Tooltip>
  );
};

export default RecalculateArgumentsButton;
